import { useState, useEffect, useCallback } from 'react'
import { Loader2 } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetDescription,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { useChildren, usePIAB, useReplacePIAB } from '@/hooks/usePIAB'
import { toast } from '@/stores/useToastStore'

interface ManagePIABDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  planId: string
  versionId: string
  parentRowId: string
  parentSkuNumber: string | null
  parentDescription: string | null
}

export function ManagePIABDrawer({
  open,
  onOpenChange,
  planId,
  versionId,
  parentRowId,
  parentSkuNumber,
  parentDescription,
}: ManagePIABDrawerProps) {
  const [quantities, setQuantities] = useState<Record<string, number>>({})

  const { data: childrenData, isLoading: isLoadingChildren } = useChildren(
    open ? planId : undefined,
    open ? versionId : undefined,
    open ? parentRowId : undefined
  )

  const { data: piabData, isLoading: isLoadingPIAB } = usePIAB(
    open ? planId : undefined,
    open ? versionId : undefined,
    open ? parentRowId : undefined
  )

  const replaceMutation = useReplacePIAB(planId, versionId)

  // Pre-fill quantities from existing PIAB config
  useEffect(() => {
    if (!open) return

    const newQuantities: Record<string, number> = {}

    if (childrenData?.children) {
      for (const child of childrenData.children) {
        newQuantities[child.id] = 1
      }
    }

    if (piabData?.items) {
      for (const item of piabData.items) {
        newQuantities[item.child_line_item_id] = item.units
      }
    }

    setQuantities(newQuantities)
  }, [open, childrenData, piabData])

  const handleQuantityChange = useCallback((childId: string, value: string) => {
    const cleaned = value.replace(/[^0-9]/g, '')
    const num = cleaned === '' ? 0 : parseInt(cleaned, 10)
    setQuantities(prev => ({ ...prev, [childId]: num }))
  }, [])

  const handleSave = useCallback(() => {
    const children = childrenData?.children ?? []
    const items = children
      .filter(child => (quantities[child.id] ?? 0) > 0)
      .map(child => ({
        child_line_item_id: child.id,
        units: quantities[child.id] ?? 1,
      }))

    if (items.length === 0) {
      toast.error('Please enter at least one quantity greater than 0')
      return
    }

    replaceMutation.mutate(
      { lineId: parentRowId, body: { items } },
      {
        onSuccess: () => {
          toast.success(
            `The PIAB for SKU #${parentSkuNumber ?? parentRowId} was successfully created`
          )
          onOpenChange(false)
        },
      }
    )
  }, [
    childrenData,
    quantities,
    replaceMutation,
    parentRowId,
    parentSkuNumber,
    onOpenChange,
  ])

  const isLoading = isLoadingChildren || isLoadingPIAB
  const isSaving = replaceMutation.isPending
  const children = childrenData?.children ?? []

  return (
    <Sheet open={open} onOpenChange={isSaving ? undefined : onOpenChange}>
      <SheetContent
        side="right"
        className="w-[540px] sm:max-w-[540px] flex flex-col"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {parentDescription ?? 'PIAB (POG In A Box)'}
            {parentSkuNumber && (
              <Badge variant="secondary">{parentSkuNumber}</Badge>
            )}
          </SheetTitle>
          <SheetDescription>
            Configure the PIAB (POG In A Box) by setting quantities for each
            child SKU.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <h3 className="text-sm font-semibold mb-3">PIAB (POG In A Box)</h3>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : children.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center px-6 py-12 border border-dashed rounded-md">
              No children assigned to this parent SKU.
              <br />
              Assign child SKUs first using the &quot;Assign Parent SKU&quot;
              action on child line items.
            </div>
          ) : (
            <div className="border rounded-md overflow-hidden">
              {/* Table header */}
              <div className="grid grid-cols-[1fr_120px] bg-muted/50 border-b">
                <div className="px-3 py-2 text-xs font-medium text-muted-foreground uppercase">
                  Child SKUs
                </div>
                <div className="px-3 py-2 text-xs font-medium text-muted-foreground uppercase text-center">
                  Quantity
                </div>
              </div>

              {/* Table rows */}
              {children.map(child => (
                <div
                  key={child.id}
                  className="grid grid-cols-[1fr_120px] border-b last:border-b-0 items-center"
                >
                  <div className="px-3 py-2.5">
                    <p className="text-sm font-medium">
                      {child.plan_sku_num ?? 'No SKU'}
                    </p>
                    {child.description && (
                      <p className="text-xs text-muted-foreground truncate">
                        {child.description}
                      </p>
                    )}
                    {child.inner_pack != null && (
                      <p className="text-xs text-muted-foreground">
                        Inner Pack: {child.inner_pack}
                      </p>
                    )}
                  </div>
                  <div className="px-3 py-2.5 flex justify-center">
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={quantities[child.id] ?? ''}
                      onChange={e =>
                        handleQuantityChange(child.id, e.target.value)
                      }
                      className="w-20 text-center h-8"
                      min={1}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <SheetFooter className="flex-row flex-shrink-0 items-center justify-end gap-2.5 border-t border-[#D6DAE1] px-6 py-6">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
            className="cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || children.length === 0}
            className="cursor-pointer"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Create PIAB'
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
